/**
 * schemas/config/policy.ts
 * The approval policy and the four configuration flags that gate the product.
 * Mirrors sk-company-cash-api/src/services/policy/policy.ts `serialize()`.
 */
import { z } from 'zod';
import { currency, minorNullable } from '../common';
import { gateStateSchema } from './gates';

export const policySchema = z.object({
  /** Every chart and every threshold is denominated in this. */
  base_currency: currency.nullable(),
  /** Spends at or above this need approval. */
  threshold_minor: minorNullable,
  /** Spends under this are never held, whatever the rules say. */
  auto_under_minor: minorNullable,
  /** Above this a SECOND approver is required after the first. */
  dual_over_minor: minorNullable,
  /**
   * Generated in Postgres as `base_currency IS NOT NULL AND threshold_minor IS
   * NOT NULL`. This is the onboarding gate: until it is true, the console is
   * replaced by the two-step setup screen.
   */
  configured: z.boolean(),
  /** Shortfall → Payroll, surplus → Billing. Reconciliation is gated on it. */
  variance_set: z.boolean(),
  /** `['payroll','billing']`. The sync log is gated on BOTH being present. */
  connectors: z.array(z.string()),
  /** Settlement alerts are gated on this. */
  offboard_feed: z.boolean(),
});
export type Policy = z.infer<typeof policySchema>;

/** PATCH /v1/config/policy — every field optional, send only what changed. */
export const policyPatchSchema = z.object({
  base_currency: currency.optional(),
  threshold_minor: z.string().optional(),
  auto_under_minor: z.string().nullable().optional(),
  dual_over_minor: z.string().nullable().optional(),
  variance_set: z.boolean().optional(),
  connectors: z.array(z.enum(['payroll', 'billing'])).optional(),
  offboard_feed: z.boolean().optional(),
});
export type PolicyPatch = z.infer<typeof policyPatchSchema>;

/** GET /v1/config/policy — the policy comes WRAPPED, with the gate map. */
export const policyResponseSchema = z.object({
  policy: policySchema,
  gates: gateStateSchema,
});
export type PolicyResponse = z.infer<typeof policyResponseSchema>;

/**
 * PATCH /v1/config/policy — verified against the live API.
 *
 * Saving the policy SEEDS option lists and approval rules, and the response
 * says how many of each appeared. That is worth surfacing rather than
 * discarding: "Approval policy saved · 1 rule created" tells the admin why the
 * Approval rules card just changed, which is the one thing about this screen
 * that would otherwise look like a glitch.
 */
export const policyPatchResultSchema = z.object({
  policy: policySchema,
  /** How many option values were seeded — a COUNT. */
  seeded_options: z.number().optional(),
  /**
   * The ids of the approval rules that were created — an ARRAY, not a count.
   * Verified against the live API: it returns `seeded_rules: []`, and typing it
   * as a number made zod reject every successful policy save.
   */
  seeded_rules: z.array(z.string()).optional(),
});
export type PolicyPatchResult = z.infer<typeof policyPatchResultSchema>;
